import type { PublicTripView } from "@ichegou/contracts";

/** Pure helpers for the public trip page; unit-tested in test/trip.test.ts. */

export type View = PublicTripView;
export type LatLng = { lat: number; lng: number };

/** `/v/{token}` locally; `/{token}` at the root of v.ichegoubr.com. */
export function tokenFromPath(pathname: string): string | null {
  const parts = pathname.split("/").filter(Boolean);
  const t = parts[0] === "v" ? parts[1] : parts.length === 1 ? parts[0] : undefined;
  return t && /^[A-Za-z0-9_-]{10,64}$/.test(t) ? t : null;
}

const time = (iso: string) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
export const fmtTime = time;

/** "5 s", "2 min" — how old the data on screen is. */
export function updatedAgo(iso: string, now: number) {
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return `há ${s} s`;
  return `há ${Math.round(s / 60)} min`;
}

export const minutes = (s: number) => `${Math.max(1, Math.round(s / 60))} min`;

const ACTIVE = new Set(["searching", "driver_assigned", "driver_arrived", "in_progress"]);
export const isLive = (v: View) => ACTIVE.has(v.status);

export type Headline = { lead: string; title: string; eta: { value: string; label: string } | null };

/** The sheet's first lines: who is going where, and the time that matters now. */
export function headline(v: View): Headline {
  const who = v.riderFirstName;
  const delivery = v.kind === "delivery";
  const eta = v.etaSeconds !== null ? minutes(v.etaSeconds) : null;
  switch (v.status) {
    case "in_progress":
      return {
        lead: delivery ? `O envio de ${who} está a caminho de` : `${who} está a caminho de`,
        title: v.dropoff.title,
        eta: v.arrivesAt ? { value: time(v.arrivesAt), label: "chegada" } : eta ? { value: eta, label: "para chegar" } : null,
      };
    case "driver_arrived":
      return { lead: delivery ? "O entregador chegou para coletar em" : `O motorista chegou para buscar ${who} em`, title: v.pickup.title, eta: null };
    case "driver_assigned":
      return {
        lead: delivery ? "O entregador está indo coletar em" : `O motorista está indo buscar ${who} em`,
        title: v.pickup.title,
        eta: eta ? { value: eta, label: "até o embarque" } : null,
      };
    case "searching":
    case "awaiting_payment":
      return { lead: delivery ? `${who} está procurando um entregador em` : `${who} está procurando um motorista em`, title: v.pickup.title, eta: null };
    case "completed":
      return { lead: delivery ? "Envio entregue em" : `${who} chegou a`, title: v.dropoff.title, eta: null };
    default:
      return { lead: delivery ? "Envio cancelado" : "Viagem cancelada", title: v.dropoff.title, eta: null };
  }
}

/** 0–1 along the leg in progress (time on board vs time still to go). */
export function progress(v: View, now: number) {
  if (v.status === "completed") return 1;
  if (v.status !== "in_progress" || !v.startedAt || v.etaSeconds === null) return v.status === "driver_arrived" ? 0.05 : 0;
  const done = Math.max(0, (now - new Date(v.startedAt).getTime()) / 1000);
  return Math.min(0.97, Math.max(0.03, done / (done + v.etaSeconds)));
}

/** "Embarcou às 00:31 na Av. Paulista · rota dentro do previsto". */
export function boardingLine(v: View) {
  if (!v.startedAt) return null;
  const route = v.routeStatus === "deviated" ? "a rota mudou do previsto" : v.routeStatus === "on_route" ? "rota dentro do previsto" : null;
  const what = v.kind === "delivery" ? `Coletado às ${time(v.startedAt)}` : `Embarcou às ${time(v.startedAt)}`;
  return `${what} na ${v.pickup.title}${route ? ` · ${route}` : ""}`;
}

/** Decodes a Google encoded polyline into lat/lng points. */
export function decodePolyline(s: string): LatLng[] {
  const out: LatLng[] = [];
  let i = 0, lat = 0, lng = 0;
  while (i < s.length) {
    for (const which of [0, 1]) {
      let shift = 0, result = 0, b: number;
      do {
        b = s.charCodeAt(i++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20 && i <= s.length);
      const d = result & 1 ? ~(result >> 1) : result >> 1;
      if (which === 0) lat += d;
      else lng += d;
    }
    out.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return out;
}

/** Index of the route point nearest to `p` (to split the route into travelled / still to go). */
export function nearestIndex(route: LatLng[], p: LatLng) {
  let best = 0, bestD = Infinity;
  route.forEach((q, i) => {
    const d = (q.lat - p.lat) ** 2 + ((q.lng - p.lng) * Math.cos((p.lat * Math.PI) / 180)) ** 2;
    if (d < bestD) (best = i), (bestD = d);
  });
  return best;
}

/** Fits lat/lng points into a w×h box (equirectangular, aspect kept) for the SVG map. */
export function projector(points: LatLng[], w: number, h: number, pad = 36) {
  const lats = points.map((p) => p.lat), lngs = points.map((p) => p.lng);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const k = Math.cos((((minLat + maxLat) / 2) * Math.PI) / 180);
  const spanX = Math.max((maxLng - minLng) * k, 1e-4), spanY = Math.max(maxLat - minLat, 1e-4);
  const scale = Math.min((w - 2 * pad) / spanX, (h - 2 * pad) / spanY);
  const ox = (w - spanX * scale) / 2, oy = (h - spanY * scale) / 2;
  return (p: LatLng) => ({ x: ox + (p.lng - minLng) * k * scale, y: oy + (maxLat - p.lat) * scale });
}

/** The SVG map's pan and zoom over its fitted drawing: screen = drawing × s + t. */
export type MapView = { s: number; tx: number; ty: number };
const MIN_ZOOM = 1, MAX_ZOOM = 8;

/** Zooms by `k` keeping the point under `p` (screen pixels) where it is. */
export function zoomAt(v: MapView, p: { x: number; y: number }, k: number): MapView {
  const s = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.s * k));
  const r = s / v.s;
  return { s, tx: p.x - (p.x - v.tx) * r, ty: p.y - (p.y - v.ty) * r };
}

/** Keeps the zoom in range and at least half the box covered by the drawing. */
export function clampView(v: MapView, w: number, h: number): MapView {
  const s = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.s));
  const clamp = (t: number, size: number) => Math.min(size / 2, Math.max(size * (1 - s) - size / 2, t));
  return { s, tx: clamp(v.tx, w), ty: clamp(v.ty, h) };
}

/** A photo link without its signature and expiry, to tell a new photo from the same one signed again. */
export function photoKey(url: string) {
  try {
    const u = new URL(url, "https://x.invalid");
    for (const k of [...u.searchParams.keys()]) if (/^(exp|sig|x-amz-.*)$/i.test(k)) u.searchParams.delete(k);
    return u.toString();
  } catch {
    return url;
  }
}
